import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@justgiving/loading/es/Loading';
import { useTracking } from '@justgiving/react-tracking';
import { useDecision } from '@optimizely/react-sdk';
import classNames from 'classnames';

import { PageStatus } from '../../../types/__generated__';
import { Experiments } from '../../constants/experiments';
import { useApp, useOptimizely, usePageMeta } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useIsMounted } from '../../hooks/useIsMounted';
import isLightColor from '../../utils/colors/isLightColor';
import { CheckoutModal } from '../CheckoutModal/CheckoutModal';
import { MODAL } from '../../context';

import styles from './DonateButton.scss';

type DonateButtonProps = {
  className?: string;
  inactivePageBehaviour?: 'disable' | 'hide';
  onClick?: () => void;
  trackingDataPageSection?: string;
  experiments?: string[];
  successType?: string;
  label?: string;
};

export const DonateButton: FunctionComponent<DonateButtonProps> = ({
  className,
  inactivePageBehaviour = 'disable',
  onClick = () => {},
  trackingDataPageSection,
  experiments,
  successType,
  label,
}) => {
  const basePageData = useBasePageQuery();
  const { currentUrl, pageType } = usePageMeta();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const track = useTracking();
  const optimizely = useOptimizely();
  const isMounted = useIsMounted();
  const { hideModal, showModal, openModal } = useApp();

  const isApplePayAvailable =
    typeof window !== 'undefined' && (window as any).ApplePaySession;

  const [{ variationKey: customBrandingFrpAbExperiment }] = useDecision(
    Experiments.CustomBrandingFrpFeature.customBrandingFrpAbExperiment
      .experimentKey,
  );
  const [{ variationKey: embeddedCheckoutAbExperiment }] = useDecision(
    Experiments.ConsumerFintech.CheckoutConversion.EmbeddedCheckoutF4[
      isApplePayAvailable ? 'applePayFlag' : 'flag'
    ],
  );

  const urlParams = new URLSearchParams(
    isMounted ? window.location.search : '',
  );
  const showEmbeddedCheckout =
    urlParams.get('embedCheckout') === 'true' ||
    embeddedCheckoutAbExperiment === 'b';

  const handleClick = () => {
    setLoading(true);

    track({
      page_section: `${pageType} page > ${trackingDataPageSection ||
        'unknown'}`.toLowerCase(),
      event: 'link click',
      action_name: 'link click',
      event_value: 'donate',
      subtype: 'donate',
    });

    if (optimizely) {
      optimizely.track(Experiments.TrackingEvents.donateButtonClick);
    }

    onClick();
  };

  const activeDonateButton = (classNames: string, href: string) => {
    if (showEmbeddedCheckout) {
      return (
        <>
          <button
            className={classNames}
            onClick={() => {
              handleClick();
              showModal({ modal: MODAL.CHECKOUT });
            }}
            data-qa="donateButton"
          >
            {label ?? t('common.donateButton')}
          </button>
          {openModal === MODAL.CHECKOUT && (
            <CheckoutModal onClose={hideModal} url={href} />
          )}
        </>
      );
    }

    return (
      <a
        className={classNames}
        onClick={handleClick}
        href={href}
        data-qa="donateButton"
      >
        {loading ? (
          <Loader size="small" colour="white" />
        ) : (
          label ?? t('common.donateButton')
        )}
      </a>
    );
  };

  if (!basePageData.data?.page) {
    return activeDonateButton(
      classNames('jg-btn', 'jg-btn--themed', styles.button, className),
      `${currentUrl}/donate`,
    );
  }
  const { status, relationships, branding } = basePageData.data.page;

  const isDonateActive = status === PageStatus.Active;

  const isMultiBeneficiaryCampaign =
    relationships && relationships.beneficiaries.totalCount > 1;

  if (inactivePageBehaviour === 'hide' && !isDonateActive) {
    return null;
  }

  const buttonType = (() => {
    if (!branding?.primaryColor) {
      return 'jg-btn--themed';
    }
    if (
      customBrandingFrpAbExperiment ===
      Experiments.CustomBrandingFrpFeature.customBrandingFrpAbExperiment
        .variations.jgDefaultColours
    ) {
      return 'jg-btn--newPrimary';
    }

    return isLightColor(branding.primaryColor)
      ? 'jg-btn--ghost'
      : 'jg-btn--themed';
  })();

  const buildQueryString = () => {
    const urlParams = new URLSearchParams();

    const trimmedSuccessType = successType?.trim();
    const trimmedExperiments = experiments
      ?.map(e => e.trim())
      .filter(e => e !== '');

    if (
      trimmedExperiments &&
      trimmedExperiments.length > 0 &&
      trimmedSuccessType
    ) {
      urlParams.append('experiments', trimmedExperiments.join(','));
      urlParams.append('successType', trimmedSuccessType);
    }

    if (showEmbeddedCheckout) {
      urlParams.append('checkoutMode', 'Headless');
    }

    return urlParams.toString() ? `?${urlParams.toString()}` : '';
  };

  const donateLink = isDonateActive ? (
    activeDonateButton(
      classNames('jg-btn', buttonType, styles.button, className),
      `${currentUrl}/donate${buildQueryString()}`,
    )
  ) : (
    <>
      <div
        className={classNames(
          'jg-btn',
          'jg-text--link',
          buttonType,
          styles.button,
          className,
          styles.disabled,
        )}
        data-qa="donateButtonDisabled"
      >
        <span aria-hidden>{label ?? t('common.donateButton')}</span>
        <span className="jg-sr-only">{t('common.donateButtonInactive')}</span>
      </div>
    </>
  );

  return !isMultiBeneficiaryCampaign ? donateLink : null;
};

export default DonateButton;
